<script setup lang="ts">
import { computed } from 'vue';
import HelpeeSignUpHeader from '@/components/sections/helpee-signup/HelpeeSignUpHeader.vue';
import HelpeeSignUpForm from '@/components/sections/helpee-signup/HelpeeSignUpForm.vue';
import { useHelpeeBranding } from '@/composables/helpee-signup/useHelpeeBranding';

interface HelperPicture {
  picture: string;
  size: string;
  margin: string;
  index: string;
}

interface HelperHighlighted {
  profilePicture?: string;
  firstname?: string;
  lastname?: string;
}

const props = withDefaults(
  defineProps<{
    headerType?: 'image' | 'community';
    headerUrl?: string;
    helpersProfilePicture?: HelperPicture[];
    helperHighlighted?: HelperHighlighted;
    hasHelpersProfilePictures?: boolean;
    widget?: boolean;
    containerWidth?: number;
    primaryColor?: string;
    rgbColor?: string;
    termsUrl?: string;
    widgetTitle?: string;
    helperNaming?: string;
    initialEmail?: string;
    isSubmitting?: boolean;
  }>(),
  {
    helpersProfilePicture: () => [],
    widget: false,
    containerWidth: 0,
  }
);

const emit = defineEmits<{
  (
    e: 'submit',
    payload: { email: string; consent: boolean; turnstileToken?: string }
  ): void;
}>();

const { headerBackgroundUrl, brandTextColor, brandLinkColor, brandColor } =
  useHelpeeBranding({
    primaryColor: props.primaryColor,
    headerUrl: props.headerUrl,
  });

const formContainerClasses = computed(() => {
  if (props.widget) {
    return props.containerWidth < 600
      ? 'px-6 pb-6 pt-10'
      : `p-10 ${props.headerType === 'community' ? 'pt-4' : ''}`;
  }

  return `px-6 pb-6 pt-10 md:p-10 ${
    props.headerType === 'community' ? 'md:pt-4' : 'md:pt-3'
  }`;
});

const handleSubmit = (payload: {
  email: string;
  consent: boolean;
  turnstileToken?: string;
}) => {
  emit('submit', payload);
};
</script>

<template>
  <HelpeeSignUpHeader
    :header-type="headerType"
    :header-background-url="headerBackgroundUrl"
    :helpers-profile-picture="helpersProfilePicture"
    :helper-highlighted="helperHighlighted"
    :has-helpers-profile-pictures="hasHelpersProfilePictures"
    :widget="widget"
    :container-width="containerWidth"
  />

  <div data-cy="signupWelcome" :class="[formContainerClasses]">
    <HelpeeSignUpForm
      :initial-email="initialEmail"
      :primary-color="primaryColor"
      :rgb-color="rgbColor"
      :terms-url="termsUrl"
      :widget-title="widgetTitle"
      :helper-naming="helperNaming"
      :brand-text-color="brandTextColor"
      :brand-link-color="brandLinkColor"
      :brand-color="brandColor"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
    />
  </div>
</template>
